/**
 * usePopups.js
 * Mounts Vue 2 components as Leaflet popup content.
 * Each popup component receives its data as a prop and has access to
 * $router, $store, and vuetify via the constructor options.
 *
 * Popup components must NOT use `scoped` styles — they're mounted outside
 * the parent component's DOM subtree.
 */
import Vue from 'vue';
import vuetify from '@/plugins/vuetify';

/**
 * @param {VueRouter} router
 * @param {VuexStore} store
 * @returns {{ mount, destroyAll }}
 */
export function createPopupManager(router, store) {
  // Track mounted instances for cleanup
  const instances = new Map(); // markerId (string) → Vue instance

  /**
   * Mount a popup Vue component and return its root DOM element.
   * @param {VueComponent}  PopupComponent - direct component reference (not a string)
   * @param {object}        data           - raw API object passed as 'data' prop
   * @param {string}        [markerId]     - optional id for instance tracking & cleanup
   * @returns {HTMLElement}
   */
  function mount(PopupComponent, data, markerId) {
    // Cleanup previous instance for the same marker
    if (markerId && instances.has(markerId)) {
      instances.get(markerId).$destroy();
      instances.delete(markerId);
    }

    const PopupClass = Vue.extend(PopupComponent);
    const instance   = new PopupClass({
      propsData: { data },
      router,
      store,
      vuetify,
    });
    instance.$mount();

    if (markerId) {
      instances.set(markerId, instance);
    }

    return instance.$el;
  }

  /**
   * Destroy all mounted popup instances.
   * Call in BaseLeafletMap's beforeDestroy().
   */
  function destroyAll() {
    instances.forEach(inst => inst.$destroy());
    instances.clear();
  }

  return { mount, destroyAll };
}
